import { usd, usd2, Odometer } from "./booking-shared";

/* ---------- Sticky order summary ---------- */
export function OrderSummary({ o, r, parcel, acres, cfg, step }) {
  const aiLabel = cfg?.all_inclusive_label || "All-Inclusive";
  const pkgName = o.package === "all_inclusive" ? aiLabel : o.package === "a_la_carte" ? "Build your own" : "Photo Only";
  const line = "flex items-center justify-between text-sm py-1.5";
  const isAla = o.package === "a_la_carte";
  // Deliverables land exactly 48 hours after the selected shoot start time.
  const deliveryEta = (() => {
    if (!o.shoot_date || !o.time_window) return null;
    const m = String(o.time_window).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    const parts = String(o.shoot_date).split("-").map(Number);
    if (!m || parts.length !== 3) return null;
    let hh = parseInt(m[1], 10) % 12;
    if (/PM/i.test(m[3])) hh += 12;
    const dt = new Date(parts[0], parts[1] - 1, parts[2], hh, parseInt(m[2], 10));
    if (isNaN(dt.getTime())) return null;
    dt.setHours(dt.getHours() + 48);
    const day = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const time = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${day} at ${time}`;
  })();
  return (
    <div className="bg-white border border-[#E9DDC6] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(26,23,21,0.04)]" data-testid="receipt">
      <div className="bg-[#4A0000] text-white px-5 py-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/60">Your order</p>
        <p className="font-medium truncate">{o.address ? o.address.split(",")[0] : "Add an address to begin"}</p>
        {o.city && <p className="text-xs text-white/60">{o.city}{o.state ? `, ${o.state}` : ""}</p>}
      </div>
      <div className="px-5 py-3">
        {step < 1 ? (
          <div className="py-6 text-center" data-testid="price-locked">
            <p className="font-display text-lg text-[#4A0000]">Your price appears next</p>
            <p className="text-xs text-[#8A8782] mt-1 leading-relaxed whitespace-nowrap">Choose a package to see pricing.</p>
          </div>
        ) : (
        <>
        {isAla ? (
          <>
            <div className={line}><span className="text-[#1A1715] font-medium">{pkgName}</span><Odometer className="font-mono text-[#1A1715]" value={r.package_price} /></div>
            {(r.line_breakdown || []).map((li) => (
              <div key={li.key} className={`${line} !py-1`}><span className="text-[#8A8782] text-xs pl-2">· {li.label}</span><span className="font-mono text-xs text-[#8A8782]">{usd(li.price)}</span></div>
            ))}
            {(!r.line_breakdown || r.line_breakdown.length === 0) && <p className="text-xs text-[#8A8782] -mt-0.5 mb-1">Pick your line items to see a price.</p>}
          </>
        ) : (
          <>
            <div className={line}><span className="text-[#1A1715] font-medium">{pkgName}</span><Odometer className="font-mono text-[#1A1715]" value={r.package_price} /></div>
            {o.package === "all_inclusive" && (!r.drone_addons || r.drone_addons.length === 0) && (
              <p className="text-xs text-[#8A8782] -mt-0.5 mb-1">Everything included — one visit</p>
            )}
            {o.package === "all_inclusive" && (r.drone_addons || []).map((li) => (
              <div key={li.key} className={`${line} !py-1`}><span className="text-[#8A8782] text-xs pl-2">· {li.label}</span><span className="font-mono text-xs text-[#8A8782]">+{usd(li.price)}</span></div>
            ))}
          </>
        )}
        {r.size_surcharge > 0 && <div className={line}><span className="text-[#5C5A58]">{r.oversized_label || "Oversized property"}</span><span className="font-mono text-[#1A1715]">+{usd(r.size_surcharge)}</span></div>}
        {r.waive_discount > 0 && <div className={line}><span className="text-[#8A6D1B]">{o.package === "photo_only" ? "Penthouse Partner rate (25%)" : "Waived 360° tour (25%)"}</span><span className="font-mono text-[#2E7D5B]">−{usd(r.waive_discount)}</span></div>}
        {r.welcome_discount > 0 && <div className={line}><span className="text-[#8A6D1B]">New-client welcome (25%)</span><span className="font-mono text-[#2E7D5B]">−{usd(r.welcome_discount)}</span></div>}
        {r.tax > 0 && <div className={line}><span className="text-[#5C5A58]">Sales tax (est.)</span><span className="font-mono text-[#1A1715]">{usd2(r.tax)}</span></div>}
        <div className="border-t border-[#F0ECE2] mt-2 pt-3 flex items-end justify-between">
          <span className="font-display text-lg text-[#4A0000]">Total today</span>
          <Odometer className="font-display text-2xl text-[#4A0000]" value={r.total_today} />
        </div>
        {deliveryEta && <p className="text-xs text-[#5C5A58] mt-1.5" data-testid="delivery">Gallery delivered by <b className="text-[#1A1715]">{deliveryEta}</b> — 48 hrs after your shoot.</p>}
        </>
        )}
      </div>

      {/* First-shoot welcome + Penthouse gift */}
      {step >= 1 && r.first_shoot_free && (
        <div className="mx-3 mb-3 rounded-lg p-3 border border-[#B08D57]/55" style={{ background: "linear-gradient(100deg, rgba(176,141,87,.18), rgba(233,199,126,.12))" }} data-testid="first-shoot-banner">
          <p className="text-xs text-[#7a5c1a] leading-snug"><b className="text-[#4A0000]">Welcome to Reelty:</b> 25% off your first shoot + Penthouse Partner (choice of an additional 25%).</p>
        </div>
      )}
    </div>
  );
}
